import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { getEmailConfirmationURL } from '@/utils/getURL';

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: 'admin' | 'student' | 'super_user';
  belt_level?: string;
  belt_level_id?: string;
  join_date?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  profile_photo_url?: string;
  age_group?: 'all' | 'kids' | 'teens' | 'adults';
  preferred_language?: string;
  dojo_id: string | null;
}

interface ImpersonationState {
  active: boolean;
  dojoId?: string;
  dojoName?: string;
  originalProfile?: UserProfile | null;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  impersonation: ImpersonationState;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (userData: RegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ success: boolean; error?: string }>;
  startImpersonation: (dojoId: string, dojoName: string) => void;
  stopImpersonation: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  role?: 'admin' | 'student' | 'super_user';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useUiLanguage();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationState>({ active: false });

  useEffect(() => {
    if (!supabase) {
      console.log('Supabase is disabled - skipping auth setup');
      setIsLoading(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth state changed:', event, Boolean(currentSession));

        const isPasswordRecovery =
          event === 'PASSWORD_RECOVERY' ||
          window.location.pathname.includes('/reset-password');

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (isPasswordRecovery) {
          // Recovery session is only for changing password.
          // Do not load profile, trial status, subscription sync, or protected app logic here.
          setIsAuthenticated(false);
          setProfile(null);
          setIsLoading(false);
          return;
        }

        setIsAuthenticated(!!currentSession);

        if (currentSession?.user) {
          setTimeout(async () => {
            await fetchUserProfile(currentSession.user.id, currentSession);
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setIsLoading(false);
        }
      }
    );

    const getInitialSession = async () => {
      const isResetPasswordPage = window.location.pathname.includes('/reset-password');

      const { data: { session: currentSession } } = await supabase.auth.getSession();

      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (isResetPasswordPage) {
        // Do not treat recovery session as normal app login.
        setIsAuthenticated(false);
        setProfile(null);
        setIsLoading(false);
        return;
      }

      setIsAuthenticated(!!currentSession);

      if (currentSession?.user) {
        await fetchUserProfile(currentSession.user.id, currentSession);
      }

      setIsLoading(false);
    };

    getInitialSession();

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    (window as any).__IMPERSONATION_CONTEXT__ = {
      active: impersonation.active,
      dojoId: impersonation.dojoId,
      dojoName: impersonation.dojoName
    };
  }, [impersonation]);

  const fetchUserProfile = async (userId: string, currentSession?: Session | null) => {
    console.log('Fetching profile for user:', userId);

    if (!supabase) {
      console.log('Supabase is disabled - skipping profile fetch');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);

        if (
          currentSession?.user?.user_metadata?.role === 'super_user' ||
          currentSession?.user?.email === 'toumpanoskleomenis@gmail.com'
        ) {
          const minimalProfile: UserProfile = {
            id: userId,
            email: currentSession.user.email || '',
            first_name: currentSession.user.user_metadata?.first_name || 'Super',
            last_name: currentSession.user.user_metadata?.last_name || 'User',
            role: 'super_user',
            age_group: 'all',
            dojo_id: null
          };

          setProfile(minimalProfile);
        }

        return;
      }

      if (!data) {
        console.log('No profile found for user:', userId);

        if (
          currentSession?.user?.user_metadata?.role === 'super_user' ||
          currentSession?.user?.email === 'toumpanoskleomenis@gmail.com'
        ) {
          const minimalProfile: UserProfile = {
            id: userId,
            email: currentSession?.user?.email || '',
            first_name: currentSession?.user?.user_metadata?.first_name || 'Super',
            last_name: currentSession?.user?.user_metadata?.last_name || 'User',
            role: 'super_user',
            age_group: 'all',
            dojo_id: null
          };

          setProfile(minimalProfile);
        }

        return;
      }

      const profileData: UserProfile = {
        ...data,
        role: data.role as 'admin' | 'student' | 'super_user',
        age_group: (data.age_group as 'all' | 'kids' | 'teens' | 'adults') || 'all'
      };

      console.log('Profile fetched successfully:', {
        email: profileData.email,
        role: profileData.role,
        id: profileData.id,
        dojo_id: profileData.dojo_id
      });

      setProfile(profileData);

      if (profileData.role === 'admin' || profileData.role === 'super_user') {
        try {
          await supabase.rpc('check_and_update_trial_status');
        } catch (trialError) {
          console.error('Error checking trial status:', trialError);
        }

        if (profileData.role === 'admin' && profileData.email && !impersonation.active) {
          try {
            const { syncSubscriptionStatus } = await import('@/utils/syncSubscriptionStatus');
            await syncSubscriptionStatus(undefined, profileData.email);
            console.log('✅ Auto-sync completed for admin user');
          } catch (syncError) {
            console.log('ℹ️ Auto-sync not available or failed:', syncError);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const login = async (email: string, password: string) => {
    if (!supabase) {
      return { success: false, error: 'Authentication is currently disabled' };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const register = async (userData: RegisterData) => {
    if (!supabase) {
      return { success: false, error: 'Registration is currently disabled' };
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            role: userData.role || 'student'
          },
          emailRedirectTo: getEmailConfirmationURL()
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user && userData.role === 'admin') {
        console.log('User registered as admin, creating dojo with trial plan...');

        const { error: dojoError } = await supabase
          .from('dojos')
          .insert({
            name: `${userData.firstName}'s Dojo`,
            created_by: data.user.id,
            plan: 'trial',
            is_active: true,
            trial_start: new Date().toISOString()
          });

        if (dojoError) {
          console.error('Error creating dojo:', dojoError);
          return { success: false, error: 'Failed to create dojo. Please contact support.' };
        }
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const logout = async () => {
    try {
      console.log('Starting logout process...');

      setUser(null);
      setProfile(null);
      setSession(null);
      setIsAuthenticated(false);

      if (!supabase) {
        console.log('Supabase is disabled - local logout complete');
        window.location.href = '/login';
        return;
      }

      const {
        data: { session: currentSession },
        error: sessionError
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.log('Session check error (ignoring):', sessionError);
        window.location.href = '/login';
        return;
      }

      if (currentSession && currentSession.access_token) {
        console.log('Valid session found, attempting server logout...');

        const { error } = await supabase.auth.signOut();

        if (error) {
          console.log('Server logout error (local state already cleared):', error);
        } else {
          console.log('Server logout successful');
        }
      } else {
        console.log('No valid session found, local logout complete');
      }

      window.location.href = '/login';
    } catch (error) {
      console.log('Logout error (local state already cleared):', error);
      window.location.href = '/login';
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    if (!supabase) {
      return { success: false, error: 'Profile updates are currently disabled' };
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) {
        return { success: false, error: error.message };
      }

      await fetchUserProfile(user.id, session);

      return { success: true };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const startImpersonation = (dojoId: string, dojoName: string) => {
    const originalProfile = profile;

    if (!originalProfile || originalProfile.role !== 'super_user') {
      console.error('Only super users can impersonate');
      return;
    }

    if (!dojoId || !dojoName) {
      console.error('Invalid dojo ID or name for impersonation');
      return;
    }

    if (impersonation.active) {
      console.warn('Already in impersonation mode. Stop current impersonation first.');
      return;
    }

    console.log(`Starting impersonation for dojo: ${dojoName} (${dojoId})`);

    try {
      setImpersonation({
        active: true,
        dojoId,
        dojoName,
        originalProfile
      });

      const impersonatedProfile: UserProfile = {
        ...originalProfile,
        role: 'admin',
        dojo_id: dojoId
      };

      setProfile(impersonatedProfile);

      (window as any).__IMPERSONATION_CONTEXT__ = {
        active: true,
        dojoId,
        dojoName
      };

      console.log('Impersonation started successfully');
    } catch (error) {
      console.error('Error starting impersonation:', error);

      setImpersonation({ active: false });

      (window as any).__IMPERSONATION_CONTEXT__ = {
        active: false,
        dojoId: null,
        dojoName: null
      };
    }
  };

  const stopImpersonation = () => {
    console.log('Stopping impersonation...');

    try {
      if (impersonation.originalProfile) {
        setProfile(impersonation.originalProfile);
      } else {
        console.warn('No original profile found during impersonation stop');

        if (user?.id) {
          fetchUserProfile(user.id, session);
        }
      }

      setImpersonation({ active: false });

      (window as any).__IMPERSONATION_CONTEXT__ = {
        active: false,
        dojoId: null,
        dojoName: null
      };

      console.log('Impersonation stopped successfully');
    } catch (error) {
      console.error('Error stopping impersonation:', error);

      setImpersonation({ active: false });

      (window as any).__IMPERSONATION_CONTEXT__ = {
        active: false,
        dojoId: null,
        dojoName: null
      };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      session,
      isAuthenticated,
      isLoading,
      impersonation,
      login,
      register,
      logout,
      updateProfile,
      startImpersonation,
      stopImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(uiText("useAuth must be used within an AuthProvider"));
  }

  return context;
};
